import React, { useEffect, useState, useCallback } from "react";
import { Reservation } from "../types/types";
import { AdminLogin } from "./AdminLogin.tsx";
import { AdminLogout } from "./AdminLogout.tsx";
import { PricingManagement } from "./PricingManagement.tsx";
import { BookingCalendar } from "./BookingCalendar.tsx";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../styles/AdminPanel.css";

interface MinimumStayRule {
  id: number;
  startDate: string;
  endDate: string;
  minimumNights: number;
  message: string;
}

export const AdminPanel: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState<{
    username: string;
    password: string;
  } | null>(null);
  const [reservations, setReservations] = useState<Reservation[]>([]);
  const [selectedReservation, setSelectedReservation] =
    useState<Reservation | null>(null);
  const [adminComment, setAdminComment] = useState("");
  const [showSettings, setShowSettings] = useState(false);
  const [showPricing, setShowPricing] = useState(false);
  const [showMinimumStay, setShowMinimumStay] = useState(false);
  const [minimumStayRules, setMinimumStayRules] = useState<MinimumStayRule[]>(
    []
  );
  const [newMinimumStayRule, setNewMinimumStayRule] = useState({
    startDate: "",
    endDate: "",
    minimumNights: 1,
    message: "",
  });
  const [settingsForm, setSettingsForm] = useState({
    currentUsername: "",
    currentPassword: "",
    newUsername: "",
    newPassword: "",
    confirmNewPassword: "",
  });
  const [editingRule, setEditingRule] = useState<MinimumStayRule | null>(null);
  const [showDateEditModal, setShowDateEditModal] = useState(false);

  const handleLogin = (username: string, password: string) => {
    setCredentials({ username, password });
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    setCredentials(null);
    setIsAuthenticated(false);
    localStorage.removeItem("adminCredentials");
  };

  const handleSettingsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setSettingsForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleUpdateCredentials = async () => {
    if (settingsForm.newPassword !== settingsForm.confirmNewPassword) {
      alert("Новите пароли не съвпадат!");
      return;
    }

    try {
      const response = await fetch(
        //"http://localhost:3331/api/admin/credentials",
        "https://api.nameren-rai.com/api/admin/credentials",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${btoa(
              `${credentials!.username}:${credentials!.password}`
            )}`,
          },
          body: JSON.stringify({
            currentUsername: settingsForm.currentUsername,
            currentPassword: settingsForm.currentPassword,
            newUsername: settingsForm.newUsername || undefined,
            newPassword: settingsForm.newPassword || undefined,
          }),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Грешка при обновяване на данните");
      }

      alert("Данните са успешно обновени!");
      setShowSettings(false);
      setSettingsForm({
        currentUsername: "",
        currentPassword: "",
        newUsername: "",
        newPassword: "",
        confirmNewPassword: "",
      });
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : "Грешка при обновяване на данните"
      );
    }
  };

  const fetchReservations = useCallback(async () => {
    if (!credentials) return;

    try {
      const response = await fetch(
        //"http://localhost:3331/api/admin/reservations",
        "https://api.nameren-rai.com/api/admin/reservations",
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${credentials.username}:${credentials.password}`
            )}`,
          },
        }
      );

      if (response.status === 401) {
        setIsAuthenticated(false);
        return;
      }

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setReservations(data);
    } catch (error) {
      console.error("Error fetching reservations:", error);
    }
  }, [credentials]);

  const fetchMinimumStayRules = useCallback(async () => {
    if (!credentials) return;

    try {
      const response = await fetch(
        //"http://localhost:3331/api/admin/minimum-stay-rules",
        "https://api.nameren-rai.com/api/admin/minimum-stay-rules",
        {
          headers: {
            Authorization: `Basic ${btoa(
              `${credentials.username}:${credentials.password}`
            )}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setMinimumStayRules(data);
    } catch (error) {
      console.error("Error fetching minimum stay rules:", error);
    }
  }, [credentials]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchReservations();
      fetchMinimumStayRules();
    }
  }, [isAuthenticated, fetchReservations, fetchMinimumStayRules]);

  if (!isAuthenticated) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  const handleStatusUpdate = async (
    id: number,
    status: "new" | "confirmed" | "pending"
  ) => {
    try {
      const response = await fetch(
        //`http://localhost:3331/api/admin/reservations/${id}`,
        `https://api.nameren-rai.com/api/admin/reservations/${id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${btoa(
              `${credentials!.username}:${credentials!.password}`
            )}`,
          },
          body: JSON.stringify({ status, adminComment }),
        }
      );
      if (response.ok) {
        fetchReservations();
        setSelectedReservation(null);
        setAdminComment("");
      }
    } catch (error) {
      console.error("Error updating reservation:", error);
    }
  };

  const handleDelete = async (id: number) => {
    if (
      window.confirm("Сигурни ли сте, че искате да изтриете тази резервация?")
    ) {
      try {
        const response = await fetch(
          //`http://localhost:3331/api/admin/reservations/${id}`,
          `https://api.nameren-rai.com/api/admin/reservations/${id}`,
          {
            method: "DELETE",
            headers: {
              Authorization: `Basic ${btoa(
                `${credentials!.username}:${credentials!.password}`
              )}`,
            },
          }
        );
        if (response.ok) {
          fetchReservations();
        }
      } catch (error) {
        console.error("Error deleting reservation:", error);
      }
    }
  };

  const handleSelectReservation = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setAdminComment(reservation.adminComment || "");
  };

  const handleSaveComment = async () => {
    if (!selectedReservation) return;

    try {
      await fetch(
        //`http://localhost:3331/api/admin/reservations/${selectedReservation.id}`,
        `https://api.nameren-rai.com/api/admin/reservations/${selectedReservation.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${btoa(
              `${credentials!.username}:${credentials!.password}`
            )}`,
          },
          body: JSON.stringify({
            status: selectedReservation.status,
            adminComment: adminComment,
          }),
        }
      );

      setReservations(
        reservations.map((res) =>
          res.id === selectedReservation.id
            ? { ...res, adminComment: adminComment }
            : res
        )
      );
    } catch (error) {
      console.error("Error saving comment:", error);
    }
  };

  const getStatusTranslation = (status: string) => {
    const translations: { [key: string]: string } = {
      new: "Нова",
      confirmed: "Потвърдена",
      pending: "Изчакване",
    };
    return translations[status] || status;
  };

  const handleMinimumStayRuleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setNewMinimumStayRule((prev) => ({
      ...prev,
      [name]: name === "minimumNights" ? parseInt(value) : value,
    }));
  };

  const handleAddMinimumStayRule = async () => {
    try {
      const response = await fetch(
        //"http://localhost:3331/api/admin/minimum-stay-rules",
        "https://api.nameren-rai.com/api/admin/minimum-stay-rules",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${btoa(
              `${credentials!.username}:${credentials!.password}`
            )}`,
          },
          body: JSON.stringify(newMinimumStayRule),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      fetchMinimumStayRules();
      setNewMinimumStayRule({
        startDate: "",
        endDate: "",
        minimumNights: 1,
        message: "",
      });
    } catch (error) {
      console.error("Error adding minimum stay rule:", error);
    }
  };

  const handleDeleteMinimumStayRule = async (id: number) => {
    try {
      const response = await fetch(
        //`http://localhost:3331/api/admin/minimum-stay-rules/${id}`,
        `https://api.nameren-rai.com/api/admin/minimum-stay-rules/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Basic ${btoa(
              `${credentials!.username}:${credentials!.password}`
            )}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      fetchMinimumStayRules();
    } catch (error) {
      console.error("Error deleting minimum stay rule:", error);
    }
  };

  const handleEditMinimumStayRule = (rule: MinimumStayRule) => {
    setEditingRule(rule);
    setNewMinimumStayRule({
      startDate: rule.startDate,
      endDate: rule.endDate,
      minimumNights: rule.minimumNights,
      message: rule.message,
    });
  };

  const handleUpdateMinimumStayRule = async () => {
    if (!editingRule) return;

    try {
      const response = await fetch(
        //`http://localhost:3331/api/admin/minimum-stay-rules/${editingRule.id}`,
        `https://api.nameren-rai.com/api/admin/minimum-stay-rules/${editingRule.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${btoa(
              `${credentials!.username}:${credentials!.password}`
            )}`,
          },
          body: JSON.stringify(newMinimumStayRule),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      fetchMinimumStayRules();
      setEditingRule(null);
      setNewMinimumStayRule({
        startDate: "",
        endDate: "",
        minimumNights: 1,
        message: "",
      });
    } catch (error) {
      console.error("Error updating minimum stay rule:", error);
    }
  };

  const handleCancelEdit = () => {
    setEditingRule(null);
    setNewMinimumStayRule({
      startDate: "",
      endDate: "",
      minimumNights: 1,
      message: "",
    });
  };

  const handleUpdateReservationDates = async () => {
    if (!selectedReservation) return;

    try {
      const response = await fetch(
        `https://api.nameren-rai.com/api/admin/reservations/${selectedReservation.id}`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Basic ${btoa(
              `${credentials!.username}:${credentials!.password}`
            )}`,
          },
          body: JSON.stringify({
            startDate: selectedReservation.startDate,
            endDate: selectedReservation.endDate,
            status: selectedReservation.status,
            adminComment: adminComment,
          }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Failed to update reservation dates");
      }

      // Refresh the reservations list
      await fetchReservations();

      // Close the modals
      setShowDateEditModal(false);
      setSelectedReservation(null);
      setAdminComment("");
    } catch (error) {
      console.error("Error updating reservation dates:", error);
      alert(
        error instanceof Error
          ? error.message
          : "Грешка при обновяване на датите на резервацията"
      );
    }
  };

  const handleDateSelection = (
    startDate: Date | null,
    endDate: Date | null
  ) => {
    if (selectedReservation && startDate && endDate) {
      setSelectedReservation({
        ...selectedReservation,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString("bg-BG", {
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  };

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <h2>Админ Панел</h2>
        <div className="admin-actions">
          <button
            className="settings-button"
            onClick={() => setShowSettings(!showSettings)}
          >
            Настройки
          </button>
          <button
            className="pricing-button"
            onClick={() => setShowPricing(!showPricing)}
          >
            {showPricing ? "Скрий цени" : "Покажи цени"}
          </button>
          <button
            className="minimum-stay-button"
            onClick={() => setShowMinimumStay(!showMinimumStay)}
          >
            {showMinimumStay
              ? "Скрий минимален престой"
              : "Покажи минимален престой"}
          </button>
          <AdminLogout onLogout={handleLogout} />
        </div>
      </div>

      {showSettings && (
        <div className="settings-modal">
          <div className="settings-content">
            <h3>Настройки на профила</h3>
            <div className="settings-form">
              <div className="form-group">
                <label>Текущо потребителско име:</label>
                <input
                  type="text"
                  name="currentUsername"
                  value={settingsForm.currentUsername}
                  onChange={handleSettingsChange}
                />
              </div>
              <div className="form-group">
                <label>Текуща парола:</label>
                <input
                  type="password"
                  name="currentPassword"
                  value={settingsForm.currentPassword}
                  onChange={handleSettingsChange}
                />
              </div>
              <div className="form-group">
                <label>Ново потребителско име:</label>
                <input
                  type="text"
                  name="newUsername"
                  value={settingsForm.newUsername}
                  onChange={handleSettingsChange}
                />
              </div>
              <div className="form-group">
                <label>Нова парола:</label>
                <input
                  type="password"
                  name="newPassword"
                  value={settingsForm.newPassword}
                  onChange={handleSettingsChange}
                />
              </div>
              <div className="form-group">
                <label>Потвърди новата парола:</label>
                <input
                  type="password"
                  name="confirmNewPassword"
                  value={settingsForm.confirmNewPassword}
                  onChange={handleSettingsChange}
                />
              </div>
              <div className="settings-buttons">
                <button onClick={handleUpdateCredentials}>
                  Запази промените
                </button>
                <button onClick={() => setShowSettings(false)}>Отказ</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {credentials && showPricing && (
        <div className={`pricing-section ${showPricing ? "show" : ""}`}>
          <PricingManagement credentials={credentials} />
        </div>
      )}

      {credentials && showMinimumStay && (
        <div className="minimum-stay-section">
          <h3>Управление на минимален престой</h3>
          <div className="minimum-stay-form">
            <div className="form-group">
              <label>Начална дата:</label>
              <input
                type="date"
                name="startDate"
                value={newMinimumStayRule.startDate}
                onChange={handleMinimumStayRuleChange}
              />
            </div>
            <div className="form-group">
              <label>Крайна дата:</label>
              <input
                type="date"
                name="endDate"
                value={newMinimumStayRule.endDate}
                onChange={handleMinimumStayRuleChange}
              />
            </div>
            <div className="form-group">
              <label>Минимален брой нощувки:</label>
              <input
                type="number"
                name="minimumNights"
                value={newMinimumStayRule.minimumNights}
                onChange={handleMinimumStayRuleChange}
                min="1"
              />
            </div>
            <div className="form-group">
              <label>Съобщение:</label>
              <textarea
                name="message"
                value={newMinimumStayRule.message}
                onChange={handleMinimumStayRuleChange}
                rows={3}
              />
            </div>
            <div className="form-buttons">
              {editingRule ? (
                <>
                  <button onClick={handleUpdateMinimumStayRule}>
                    Запази промените
                  </button>
                  <button onClick={handleCancelEdit}>Отказ</button>
                </>
              ) : (
                <button onClick={handleAddMinimumStayRule}>
                  Добави правило
                </button>
              )}
            </div>
          </div>

          <div className="minimum-stay-rules">
            <h4>Съществуващи правила</h4>
            <table>
              <thead>
                <tr>
                  <th>Начална дата</th>
                  <th>Крайна дата</th>
                  <th>Минимален брой нощувки</th>
                  <th>Съобщение</th>
                  <th>Действия</th>
                </tr>
              </thead>
              <tbody>
                {minimumStayRules.map((rule) => (
                  <tr key={rule.id}>
                    <td>{new Date(rule.startDate).toLocaleDateString()}</td>
                    <td>{new Date(rule.endDate).toLocaleDateString()}</td>
                    <td>{rule.minimumNights}</td>
                    <td>{rule.message}</td>
                    <td>
                      <button onClick={() => handleEditMinimumStayRule(rule)}>
                        Редактирай
                      </button>
                      <button
                        onClick={() => handleDeleteMinimumStayRule(rule.id)}
                      >
                        Изтрий
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="reservations-table">
        <table>
          <thead>
            <tr>
              <th>Дата на създаване</th>
              <th>Период</th>
              <th>Име</th>
              <th>Телефон</th>
              <th>Имейл</th>
              <th>Статус</th>
              <th>Обща цена</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {reservations.map((reservation) => (
              <tr key={reservation.id}>
                <td>{new Date(reservation.createdAt).toLocaleString()}</td>
                <td>
                  {new Date(reservation.startDate).toLocaleDateString()} -{" "}
                  {new Date(reservation.endDate).toLocaleDateString()}
                </td>
                <td>
                  {reservation.firstName} {reservation.lastName}
                </td>
                <td>{reservation.phone}</td>
                <td>{reservation.email}</td>
                <td>{getStatusTranslation(reservation.status)}</td>
                <td>{reservation.totalPrice}€</td>
                <td>
                  <button onClick={() => handleSelectReservation(reservation)}>
                    Детайли
                  </button>
                  <button onClick={() => handleDelete(reservation.id)}>
                    Изтрий
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {selectedReservation && !showDateEditModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h2>Детайли за резервация</h2>
            <div className="reservation-info">
              <div className="info-group">
                <label>Дата на настаняване:</label>
                <p>{formatDate(new Date(selectedReservation.startDate))}</p>
              </div>
              <div className="info-group">
                <label>Дата на напускане:</label>
                <p>
                  {formatDate(
                    new Date(
                      new Date(selectedReservation.endDate).getTime() +
                        24 * 60 * 60 * 1000
                    )
                  )}
                </p>
              </div>
              <div className="info-group">
                <label>Име:</label>
                <p>
                  {selectedReservation.firstName} {selectedReservation.lastName}
                </p>
              </div>
              <div className="info-group">
                <label>Телефон:</label>
                <p>{selectedReservation.phone}</p>
              </div>
              <div className="info-group">
                <label>Имейл:</label>
                <p>{selectedReservation.email}</p>
              </div>
              <div className="info-group">
                <label>Обща цена:</label>
                <p>{selectedReservation.totalPrice}€</p>
              </div>
              <div className="info-group">
                <label>Статус:</label>
                <select
                  value={selectedReservation.status}
                  onChange={(e) => {
                    setSelectedReservation({
                      ...selectedReservation,
                      status: e.target.value as "new" | "confirmed" | "pending",
                    });
                  }}
                >
                  <option value="new">Нова</option>
                  <option value="confirmed">Потвърдена</option>
                  <option value="pending">Изчакване</option>
                </select>
              </div>
              <div className="info-group">
                <label>Коментар:</label>
                <textarea
                  value={adminComment}
                  onChange={(e) => setAdminComment(e.target.value)}
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setShowDateEditModal(true)}
                className="edit-button"
              >
                Промени дати
              </button>
              <button
                onClick={handleUpdateReservationDates}
                className="save-button"
              >
                Запази промените
              </button>
              <button
                onClick={() => setSelectedReservation(null)}
                className="close-button"
              >
                Затвори
              </button>
            </div>
          </div>
        </div>
      )}

      {selectedReservation && showDateEditModal && (
        <div className="modal-overlay">
          <div className="modal-content date-edit-modal">
            <h2>Промяна на дати</h2>
            <div className="calendar-section">
              <BookingCalendar
                existingReservations={reservations.filter(
                  (r) => r.id !== selectedReservation.id
                )}
                onDateSelection={handleDateSelection}
              />
            </div>
            <div className="modal-actions">
              <button
                onClick={() => setShowDateEditModal(false)}
                className="cancel-button"
              >
                Отказ
              </button>
              <button
                onClick={() => {
                  handleUpdateReservationDates();
                  setShowDateEditModal(false);
                }}
                className="save-button"
              >
                Запази
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
